import { useState } from "react";
import { api, downloadBlob, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Path, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRIORITIES = ["Priority 1", "Priority 2", "Priority 3"];

export default function FieldPlanPanel({ project, reload }) {
  const id = project.id;
  const plan = project.field_plan;
  const [prios, setPrios] = useState(["Priority 1", "Priority 2"]);
  const [spacing, setSpacing] = useState(150);
  const [access, setAccess] = useState("");
  const [camp, setCamp] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (p) => setPrios((x) => x.includes(p) ? x.filter((y) => y !== p) : [...x, p]);
  const parseLL = (s) => {
    const v = s.trim().split(/[,\s]+/).map(Number).filter((x) => !isNaN(x));
    return v.length >= 2 ? [v[1], v[0]] : null; // "lat, lon" -> [lon, lat]
  };

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${id}/fieldplan`, {
        priorities: prios, spacing_m: Number(spacing),
        access_point: parseLL(access), camp: parseLL(camp),
      });
      toast.success("Field programme generated");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const dl = async (fmt) => {
    try { await downloadBlob(`/projects/${id}/fieldplan/${fmt}`, `fieldplan_${project.name}.${fmt}`); toast.success("Download started"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="border-t border-border pt-4 space-y-3">
      <p className="overline">Field Verification Planning</p>
      <div>
        <Label className="text-xs">Include priorities</Label>
        <div className="flex gap-2 mt-1">
          {PRIORITIES.map((p) => (
            <button key={p} onClick={() => toggle(p)} data-testid={`fp-prio-${p.split(" ")[1]}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${prios.includes(p) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{p}</button>
          ))}
        </div>
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1"><Label className="text-xs">Sample spacing (m)</Label>
          <Input type="number" value={spacing} onChange={(e) => setSpacing(e.target.value)} className="h-9 mt-1" data-testid="fp-spacing" /></div>
        <Button onClick={generate} disabled={busy} data-testid="fp-generate">
          {busy ? <CircleNotch size={16} className="animate-spin" /> : <><Path size={16} className="mr-1" />Generate</>}
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Access point (lat, lon)</Label>
          <Input value={access} onChange={(e) => setAccess(e.target.value)} className="h-9 mt-1 font-mono text-xs" placeholder="optional" data-testid="fp-access" /></div>
        <div><Label className="text-xs">Camp (lat, lon)</Label>
          <Input value={camp} onChange={(e) => setCamp(e.target.value)} className="h-9 mt-1 font-mono text-xs" placeholder="optional" data-testid="fp-camp" /></div>
      </div>

      {plan && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="border border-border rounded p-2"><div className="font-mono font-semibold">{plan.stats?.targets}</div><div className="overline">Targets</div></div>
            <div className="border border-border rounded p-2"><div className="font-mono font-semibold">{plan.stats?.sample_points}</div><div className="overline">Sample pts</div></div>
            <div className="border border-border rounded p-2"><div className="font-mono font-semibold">{plan.routes?.length}</div><div className="overline">Traverses</div></div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {["gpx", "kml", "csv", "geojson"].map((f) => (
              <Button key={f} size="sm" variant="outline" className="h-8 text-xs uppercase" onClick={() => dl(f)} data-testid={`fp-export-${f}`}>{f}</Button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">Sampling points and traverse routes are drawn on the map. Export GPX to load directly into field GPS units.</p>
        </div>
      )}
    </div>
  );
}
